import CloseIcon from '@mui/icons-material/Close'
import GradingIcon from '@mui/icons-material/Grading'
import ListIcon from '@mui/icons-material/List'
import SearchIcon from '@mui/icons-material/Search'

import LocalStorageButtons from '@components/LocalStorageButtons'
import FilterFromToDate from '@components/toolbar/FilterFromToDate'
import FilterFromToWeek from '@components/toolbar/FilterFromToWeek'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import FilterKpi from '@components/toolbar/FilterKpi'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectGebied } from '@components/toolbar/FilterSelectGebied'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import { FilterSelectString } from '@components/toolbar/FilterSelectString'
import { FilterToggleButton } from '@components/toolbar/FilterToggleButton'
import type { FilterOption } from '@models/Filter'
import type { Gebied } from '@models/Gebied'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { queryKey } from '../hooks/useAssignedBijlagenSearch'
import type { AssignedBijlagenSearchFilters as Filters } from '../types/AssignedBijlagenSearch'

const documentsoorten = [
  'Bodemonderzoek',
  'Bodemrapport',
  'Bodemvooronderzoek',
  'CROW 500',
  'Combi schetsontwerp',
  'Milieuprocedure',
  'Overig',
  'Resultaten proefsleuven',
  'Tracétekening bodem',
  'Tracétekening vergunning',
  'Tracétekening vergunning GV',
  'VGM-plan/RI&E',
  'Vergunning',
  'Verslag klantcontact',
  'Werkmap',
  'Werkmap GV',
]

const kpiTooltips = {
  red: 'Laat alleen de verlopen bijlagen zien',
  amber: 'Laat bijna verlopen bijlagen zien',
  green: 'Laat alle geplande bijlagen zien die niet (bijna) verlopen zijn',
  grey: 'Laat alle bijlagen zien',
}

interface Props {
  onChange: (filters: Filters) => void
  filters: Filters
  initialFilters: Filters
  onRefresh: () => void
  onReset: () => void
  filterOptions: {
    coordinerendeAannemers: string[]
    netbeheerders: string[]
    gebieden: Gebied[]
    labels: FilterOption[]
  }
}

export const AssignedBijlagenFilters = ({
  onChange,
  filters,
  initialFilters,
  onRefresh,
  onReset,
  filterOptions: {
    coordinerendeAannemers,
    netbeheerders,
    gebieden,
    labels: valueListLabels,
  },
}: Props) => {
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })

  const {
    gebied,
    documentsoort,
    assignedTo,
    fromWeek,
    toWeek,
    fromGeplandeUitvoeringsdatum,
    toGeplandeUitvoeringsdatum,
    labels,
    review,
    myList,
    kpi,
  } = localFilters
  const { combi } = useAuthenticatedUser()

  const buildOption = (group) => (label) => ({ group, label, code: label })
  const assignees = (combi ? [buildOption('Combi')(combi)] : [])
    .concat(netbeheerders.map(buildOption('Netbeheerder')))
    .concat(coordinerendeAannemers.map(buildOption('Coordinerende')))

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectString
          multiple
          options={documentsoorten}
          label="Documentsoort"
          value={documentsoort}
          filterKey="documentsoort"
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectGebied
          options={gebieden}
          value={gebied}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      {combi && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={assignees}
            label="Toegewezen aan"
            filterKey="assignedTo"
            value={assignedTo}
            onFilterChange={handleFilterChange}
            groupByProp="group"
          />
        </FilterGridItem>
      )}

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          fromDate={fromGeplandeUitvoeringsdatum}
          toDate={toGeplandeUitvoeringsdatum}
          onFilterChange={handleFilterChange}
          label="Project gepland"
          prop="GeplandeUitvoeringsdatum"
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToWeek
          fromWeek={fromWeek}
          toWeek={toWeek}
          onFilterChange={handleFilterChange}
          label="Opleverweek"
        />
      </FilterGridItem>

      {valueListLabels.length > 0 && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={valueListLabels}
            label="Labels"
            filterKey="labels"
            value={labels}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <FilterKpi
          onFilterChange={handleFilterChange}
          kpi={kpi}
          tooltips={kpiTooltips}
        />
        <StyledIconButton
          icon={<CloseIcon />}
          title="Reset filters"
          onClick={handleReset}
        />
        <StyledIconButton
          icon={<SearchIcon />}
          title="Zoeken"
          onClick={handleZoeken}
        />

        <FilterMyList myList={myList} onFilterChange={handleFilterChange} />

        <FilterToggleButton
          filterKey="review"
          value={review}
          onFilterChange={handleFilterChange}
          activeIcon={<ListIcon />}
          inactiveIcon={<GradingIcon />}
          activeTitle="Klik om alle bijlagen te tonen"
          inactiveTitle="Klik om alleen te beoordelen bijlagen te tonen"
        />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
